// BB.2 — Home "Meu Negócio" = Plano de Negócio (fonte canônica).
//
// Substitui a versão original que apenas ecoava blocos jsonb crus e
// mostrava um CTA genérico. Agora:
// - Progresso vem do `buildBusinessPlan` (adapter BB.1)
// - CTA principal vem de `resolveNextBusinessPlanStep`
// - Cards têm variantes empty/in_progress/complete/attention/blocked com
//   ação contextual gerada por `resolveBusinessPlanEditor` (BB.2)
// - Toggle MODO GUIADO ↔ MODO PLANO (padrão guiado, persistido em
//   localStorage — sem nova tabela)
//
// A rota antiga /membros/meu-negocio continua o ponto de entrada — não
// quebra links da home nem menus laterais.

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, Briefcase, CheckCircle2, Clock, Compass, Layers,
  Lock, Sparkles, AlertTriangle, Circle,
} from "lucide-react";
import { getBusinessPlanBundle } from "@/lib/business-plan/editor.functions";
import { CHAPTER_LABELS, CHAPTER_ORDER, editorialLabel, SECTION_BY_KEY, type BlockKey } from "@/lib/business-plan/schema";
import type { SectionGroup } from "@/lib/business-plan/schema";
import type { PopulatedSection } from "@/lib/business-plan/adapter";
import type { SectionEditorResolution } from "@/lib/business-plan/editor-resolver";
import type { NextBusinessPlanStep } from "@/lib/business-plan/resolver";
import { track } from "@/hooks/useAnalytics";
import { ensureBuilderSession } from "@/lib/homologacao.tracking";
import { HomologacaoObserver } from "@/components/business-builder/HomologacaoObserver";
import { AIReviewButton } from "@/components/ai/AIReviewButton";

export const Route = createFileRoute("/_authenticated/membros/meu-negocio/")({
  head: () => ({
    meta: [
      { title: "Meu Negócio — Projeto 101" },
      { name: "description", content: "Seu plano de negócio vivo: cada seção construída, com o próximo passo destacado." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MeuNegocioPlan,
});

type ViewMode = "guiado" | "plano";
const MODE_STORAGE_KEY = "bp:view_mode";

function MeuNegocioPlan() {
  const fetchBundle = useServerFn(getBusinessPlanBundle);
  const { data, isLoading } = useQuery({
    queryKey: ["business-plan-bundle"],
    queryFn: () => fetchBundle(),
  });

  const [mode, setMode] = useState<ViewMode>("guiado");
  useEffect(() => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === "guiado" || stored === "plano") setMode(stored);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    ensureBuilderSession();
    if (!data?.hasProject || !data.plan) return;
    const done = data.plan.sections.filter((s) => s.status === "complete").length;
    track("builder_summary_viewed", {
      metadata: {
        mode,
        sections_total: data.plan.sections.length,
        sections_done: done,
        next_reason: data.nextStep?.reason ?? null,
      },
    });
  }, [data, mode]);

  if (isLoading) {
    return <div className="mx-auto h-96 max-w-5xl animate-pulse rounded-2xl bg-black/5" />;
  }

  if (!data || !data.hasProject) {
    return <EmptyState />;
  }

  const { business, plan, editorsBySection, nextStep } = data;
  if (!plan || !editorsBySection) return <EmptyState />;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Header
        business={business}
        plan={plan}
        mode={mode}
        onModeChange={setMode}
      />

      <ChapterProgress plan={plan} />

      {nextStep && (
        <NextStepBanner
          step={nextStep}
          resolution={editorsBySection[nextStep.sectionKey]}
        />
      )}

      {mode === "guiado" ? (
        <GuidedFocus
          plan={plan}
          editorsBySection={editorsBySection}
          nextStep={nextStep}
        />
      ) : (
        <PlanGrid plan={plan} editorsBySection={editorsBySection} />
      )}
      <HomologacaoObserver sectionKey="resumo" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-8 text-center">
      <Briefcase size={28} className="mx-auto text-[var(--gray-ink)]" />
      <h1 className="mt-3 font-display text-xl font-extrabold text-[var(--navy)]">
        Você ainda não tem um negócio em construção
      </h1>
      <p className="mt-2 text-sm text-[var(--gray-ink)]">
        Escolha um negócio na biblioteca para começar. Seu plano vivo fica salvo aqui.
      </p>
      <Link
        to="/membros/negocios"
        className="mt-5 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
      >
        Explorar biblioteca <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Header({
  business, plan, mode, onModeChange,
}: {
  business: any;
  plan: NonNullable<ReturnType<typeof useBundleShape>>["plan"];
  mode: ViewMode;
  onModeChange: (m: ViewMode) => void;
}) {
  const pct = Math.round((plan?.progress.weighted ?? 0) * 100);
  const p = plan!.progress;
  return (
    <header className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Seu plano de negócio
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-[var(--navy)] lg:text-4xl">
            {business?.title ?? "Meu negócio"}
          </h1>
          {business?.summary && (
            <p className="mt-2 max-w-3xl text-sm text-[var(--gray-ink)]">{business.summary}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AIReviewButton />
          <Link to="/membros/meu-negocio/plano"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]">
            <Layers size={12} /> Plano completo
          </Link>
          <Link to="/membros/meu-negocio/pendencias"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]">
            <AlertTriangle size={12} /> Pendências
          </Link>
          <ModeToggle mode={mode} onChange={onModeChange} />
        </div>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div>
          <div className="flex items-baseline justify-between text-xs">
            <span className="font-semibold text-[var(--gray-ink)]">Construção do plano</span>
            <span className="font-display text-lg font-extrabold text-[var(--navy)]">{pct}%</span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
            <div className="h-full bg-[var(--green)] transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <ProgressChip label="completas" value={p.complete} tone="green" />
          <ProgressChip label="em construção" value={p.inProgress} tone="navy" />
          <ProgressChip label="com atenção" value={p.attention} tone="amber" />
          <ProgressChip label="bloqueadas" value={p.blocked} tone="gray" />
          <ProgressChip label="não iniciadas" value={p.empty} tone="gray" />
        </div>
      </div>
    </header>
  );
}
function useBundleShape() { return null as unknown as NonNullable<Awaited<ReturnType<ReturnType<typeof useServerFn<typeof getBusinessPlanBundle>>>>>; }

function ModeToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="inline-flex rounded-full border border-black/10 bg-white p-0.5 text-xs">
      <button
        onClick={() => onChange("guiado")}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition ${
          mode === "guiado" ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)]"
        }`}
      >
        <Compass size={12} /> Guiado
      </button>
      <button
        onClick={() => onChange("plano")}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition ${
          mode === "plano" ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)]"
        }`}
      >
        <Layers size={12} /> Plano completo
      </button>
    </div>
  );
}

function ProgressChip({
  label, value, tone,
}: { label: string; value: number; tone: "green" | "navy" | "amber" | "gray" }) {
  const toneClass =
    tone === "green" ? "bg-[var(--green)]/10 text-[var(--green)]" :
    tone === "navy" ? "bg-[var(--navy)]/10 text-[var(--navy)]" :
    tone === "amber" ? "bg-amber-100 text-amber-700" :
    "bg-black/5 text-[var(--gray-ink)]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-semibold ${toneClass}`}>
      <strong className="font-display">{value}</strong> {label}
    </span>
  );
}

function NextStepBanner({
  step, resolution,
}: {
  step: NextBusinessPlanStep;
  resolution?: SectionEditorResolution;
}) {
  const section = SECTION_BY_KEY[step.sectionKey];
  const cta = ctaHref(resolution, step);
  return (
    <section className="relative overflow-hidden rounded-3xl border border-[var(--navy)]/15 bg-gradient-to-br from-[var(--navy)]/[0.04] to-white p-6 shadow-sm lg:p-8">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--navy)]/70">
        <Sparkles size={12} />
        {step.finished ? "Plano estruturado" : "Próximo passo de construção"}
      </div>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
        {step.ctaLabel}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-[var(--gray-ink)]">
        {step.description}
      </p>
      <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-[var(--gray-ink)]">
        <span className="inline-flex items-center gap-1"><Clock size={11} /> {step.estimatedMinutes} min</span>
        {section && <span>Seção: {section.title}</span>}
        {resolution && (
          <span className="inline-flex items-center gap-1">
            Modo: <strong className="text-[var(--navy)]">{modeLabel(resolution.mode)}</strong>
          </span>
        )}
      </div>
      <p className="mt-3 text-xs text-[var(--gray-ink)]/90">
        <strong className="text-[var(--navy)]">Por que agora:</strong> {step.expectedImpact}
      </p>
      {cta && (
        <div className="mt-5">
          <CtaLink to={cta.to} params={cta.params}>
            {resolution?.cta ?? step.ctaLabel} <ArrowRight size={14} />
          </CtaLink>
        </div>
      )}
    </section>
  );
}

function GuidedFocus({
  plan, editorsBySection, nextStep,
}: {
  plan: NonNullable<ReturnType<typeof useBundleShape>>["plan"];
  editorsBySection: Record<BlockKey, SectionEditorResolution>;
  nextStep: NextBusinessPlanStep | null;
}) {
  // Mostra a seção-alvo em destaque + próximas 2-3 em prévia.
  if (!plan) return null;
  const focusKey = nextStep?.sectionKey ?? plan.sections[0]?.section.key;
  const focus = plan.sections.find((s) => s.section.key === focusKey);
  const others = plan.sections.filter((s) => s.section.key !== focusKey).slice(0, 3);
  return (
    <div className="space-y-5">
      {focus && (
        <SectionCard
          section={focus}
          resolution={editorsBySection[focus.section.key]}
          featured
        />
      )}
      {others.length > 0 && (
        <>
          <p className="pt-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Também no seu plano
          </p>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {others.map((s) => (
              <SectionCard
                key={s.section.key}
                section={s}
                resolution={editorsBySection[s.section.key]}
              />
            ))}
          </div>
          <div className="pt-3 text-center text-xs text-[var(--gray-ink)]">
            Precisa ver tudo? Alterne para o modo <strong>Plano completo</strong> acima.
          </div>
        </>
      )}
    </div>
  );
}

function PlanGrid({
  plan, editorsBySection,
}: {
  plan: NonNullable<ReturnType<typeof useBundleShape>>["plan"];
  editorsBySection: Record<BlockKey, SectionEditorResolution>;
}) {
  if (!plan) return null;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {plan.sections.map((s) => (
        <SectionCard
          key={s.section.key}
          section={s}
          resolution={editorsBySection[s.section.key]}
        />
      ))}
    </div>
  );
}

function SectionCard({
  section, resolution, featured,
}: {
  section: PopulatedSection;
  resolution?: SectionEditorResolution;
  featured?: boolean;
}) {
  const s = section.section;
  const status = section.status;
  const tone = STATUS_TONE[status];
  const cta = ctaHref(resolution, null, section.section.key);

  return (
    <article
      className={`flex flex-col rounded-2xl border p-5 transition ${
        featured
          ? "border-[var(--navy)]/20 bg-white shadow-lg shadow-[var(--navy)]/5 lg:p-6"
          : `${tone.border} bg-white`
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone.badge}`}
          >
            {tone.icon} {STATUS_LABEL[status]}
          </span>
          <h3 className={`mt-2 font-display font-extrabold text-[var(--navy)] ${featured ? "text-xl" : "text-base"}`}>
            {s.title}
          </h3>
          <p className="mt-0.5 text-xs text-[var(--gray-ink)]">{s.description}</p>
        </div>
      </header>

      <div className="mt-4 flex-1 space-y-2">
        {status === "empty" && (
          <p className="rounded-lg bg-[var(--gray-soft)]/60 p-3 text-xs text-[var(--gray-ink)]">
            Nenhuma resposta salva. {s.whyItMatters}
          </p>
        )}
        {status === "in_progress" && (
          <>
            <FilledPreview section={section} />
            {section.missingRequired.length > 0 && (
              <p className="text-[11px] text-[var(--gray-ink)]">
                Falta: {section.missingRequired.map(editorialLabel).join(", ")}.
              </p>
            )}
          </>
        )}
        {status === "complete" && <FilledPreview section={section} />}
        {status === "attention" && (
          <div className="space-y-2">
            <FilledPreview section={section} />
            <p className="rounded-lg bg-amber-50 p-2.5 text-[11px] text-amber-800">
              {section.reasons[0] ?? "Revise as respostas para concluir esta seção."}
            </p>
          </div>
        )}
        {status === "blocked" && resolution?.dependencySectionKey && (
          <p className="rounded-lg bg-black/5 p-3 text-xs text-[var(--gray-ink)]">
            Depende de <strong className="text-[var(--navy)]">{SECTION_BY_KEY[resolution.dependencySectionKey]?.title}</strong>.
          </p>
        )}
      </div>

      <div className="mt-4">
        {resolution?.mode === "unavailable" ? (
          <p className="text-[11px] text-[var(--gray-ink)]">
            Editor indisponível para esta seção.
          </p>
        ) : cta ? (
          <CtaLink to={cta.to} params={cta.params} secondary={!featured}>
            {resolution?.cta ?? "Vamos construir"} <ArrowRight size={12} />
          </CtaLink>
        ) : null}
      </div>
    </article>
  );
}

function FilledPreview({ section }: { section: PopulatedSection }) {
  const rows = section.filled.filter((r) => !r.long).slice(0, 3);
  if (rows.length === 0) return null;
  return (
    <dl className="space-y-1.5 text-xs">
      {rows.map((r) => (
        <div key={r.key} className="grid grid-cols-[110px_1fr] gap-2">
          <dt className="truncate text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            {r.label}
          </dt>
          <dd className="truncate text-[var(--navy)]">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------- helpers ----------

function ctaHref(
  resolution: SectionEditorResolution | undefined,
  step: NextBusinessPlanStep | null,
  fallbackSectionKey?: BlockKey,
): { to: any; params: any } | null {
  const sectionKey = resolution?.sectionKey ?? step?.sectionKey ?? fallbackSectionKey;
  if (!sectionKey) return null;
  if (resolution?.mode === "guided" && resolution.missionId) {
    return { to: "/membros/meu-projeto/missao/$id", params: { id: resolution.missionId } };
  }
  if (resolution?.mode === "dependency" && resolution.dependencySectionKey) {
    return {
      to: "/membros/meu-negocio/secao/$section",
      params: { section: resolution.dependencySectionKey },
    };
  }
  if (resolution?.mode === "unavailable") return null;
  // structured (default)
  return { to: "/membros/meu-negocio/secao/$section", params: { section: sectionKey } };
}

function CtaLink({
  to, params, children, secondary,
}: { to: any; params: any; children: React.ReactNode; secondary?: boolean }) {
  const cls = secondary
    ? "inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
    : "inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 transition hover:-translate-y-0.5";
  return (
    <Link to={to} params={params} className={cls}>
      {children}
    </Link>
  );
}

function modeLabel(m: SectionEditorResolution["mode"]): string {
  switch (m) {
    case "guided": return "guiado";
    case "structured": return "estruturado";
    case "dependency": return "dependência";
    case "unavailable": return "indisponível";
  }
}

const STATUS_LABEL: Record<PopulatedSection["status"], string> = {
  empty: "Não iniciada",
  in_progress: "Em construção",
  complete: "Completa",
  attention: "Precisa revisar",
  blocked: "Bloqueada",
};

const STATUS_TONE: Record<PopulatedSection["status"], { border: string; badge: string; icon: React.ReactNode }> = {
  empty: { border: "border-dashed border-black/10", badge: "border-black/10 bg-black/5 text-[var(--gray-ink)]", icon: <Circle size={10} /> },
  in_progress: { border: "border-[var(--navy)]/20", badge: "border-[var(--navy)]/20 bg-[var(--navy)]/[0.06] text-[var(--navy)]", icon: <Compass size={10} /> },
  complete: { border: "border-[var(--green)]/25", badge: "border-[var(--green)]/25 bg-[var(--green)]/10 text-[var(--green)]", icon: <CheckCircle2 size={10} /> },
  attention: { border: "border-amber-300", badge: "border-amber-300 bg-amber-100 text-amber-800", icon: <AlertTriangle size={10} /> },
  blocked: { border: "border-black/10", badge: "border-black/10 bg-black/5 text-[var(--gray-ink)]", icon: <Lock size={10} /> },
};

// ---------- Chapter progress bar (BB.4) ----------

const CHAPTER_TONE: Record<PopulatedSection["status"], string> = {
  complete: "bg-[var(--green)]",
  in_progress: "bg-[var(--navy)]",
  attention: "bg-amber-400",
  blocked: "bg-black/25",
  empty: "bg-black/10",
};

function ChapterProgress({
  plan,
}: {
  plan: NonNullable<ReturnType<typeof useBundleShape>>["plan"];
}) {
  if (!plan) return null;
  const byGroup = new Map<SectionGroup, PopulatedSection[]>();
  for (const s of plan.sections) {
    const g = s.section.group;
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(s);
  }
  const chapters = CHAPTER_ORDER
    .map((g) => ({ id: g, label: CHAPTER_LABELS[g], sections: byGroup.get(g) ?? [] }))
    .filter((c) => c.sections.length > 0);

  return (
    <section className="rounded-2xl border border-black/5 bg-white p-5 lg:p-6">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          Capítulos do plano
        </p>
        <p className="text-[11px] text-[var(--gray-ink)]">Cada capítulo é um bloco da construção</p>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {chapters.map((c) => {
          const total = c.sections.length;
          const complete = c.sections.filter((s) => s.status === "complete").length;
          const partial = c.sections.filter((s) => s.status === "in_progress" || s.status === "attention").length;
          const pct = total === 0 ? 0 : Math.round(((complete + partial * 0.4) / total) * 100);
          return (
            <div key={c.id} className="rounded-xl border border-black/5 bg-[var(--beige)]/40 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[var(--navy)]">{c.label}</p>
                <span className="font-display text-sm font-extrabold text-[var(--navy)]">{pct}%</span>
              </div>
              <div className="mt-2 flex gap-1">
                {c.sections.map((s) => (
                  <span
                    key={s.section.key}
                    title={`${s.section.title}: ${STATUS_LABEL[s.status]}`}
                    className={`h-1.5 flex-1 rounded-full ${CHAPTER_TONE[s.status]}`}
                  />
                ))}
              </div>
              <p className="mt-2 text-[10px] text-[var(--gray-ink)]">
                {complete}/{total} construídas
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

